import { authClientWeb } from "react-redux-django-auth/web";

export type VisitorStatus = "CHECKED_IN" | "ENQUIRY" | "CHECKED_OUT" | "CANCELLED";

export interface VisitorLogItem {
  id: number;
  visitor_id: string;
  full_name: string;
  phone_number: string;
  email?: string;
  purpose: string;
  whom_to_see?: string;
  status: VisitorStatus;
  status_display?: string;
  date: string;
  check_in_time: string;
  check_out_time?: string | null;
  duration_minutes?: number | null;
  notes?: string;
  logged_by?: number | null;
  logged_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface VisitorKPIs {
  total_visitors: number;
  today_visitors: number;
  currently_onsite: number;
  active_enquiries: number;
  total_checked_out: number;
  total_cancelled: number;
}

export interface VisitorQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  status?: string;
  purpose?: string;
  logged_by?: string | number;
  start_date?: string;
  end_date?: string;
  scope?: "personal";
}

export interface VisitorListResponse {
  success: boolean;
  message?: string;
  data: VisitorLogItem[];
  count: number;
  next: string | null;
  previous: string | null;
  analytics: VisitorKPIs;
}

export interface CreateVisitorPayload {
  full_name: string;
  phone_number: string;
  email?: string;
  purpose: string;
  whom_to_see?: string;
  status?: VisitorStatus;
  date?: string;
  notes?: string;
}

export interface UpdateVisitorPayload {
  full_name?: string;
  phone_number?: string;
  email?: string;
  purpose?: string;
  whom_to_see?: string;
  status?: VisitorStatus;
  notes?: string;
  action?: "check_out" | "check_in";
}

const getClient = () => {
  try {
    const client = authClientWeb();
    if (client) return client;
  } catch {
    // fallback if authClientWeb is not initialized yet
  }
};

/**
 * Fetch paginated list of visitors with analytics KPIs
 */
export const fetchVisitors = async (
  params: VisitorQueryParams = {},
  signal?: AbortSignal
): Promise<VisitorListResponse> => {
  const client = getClient();
  if (!client) throw new Error("API client not initialized");

  const query = new URLSearchParams();
  if (params.page) query.append("page", params.page.toString());
  if (params.page_size) query.append("page_size", params.page_size.toString());
  if (params.search) query.append("search", params.search);
  if (params.status && params.status !== "ALL") query.append("status", params.status);
  if (params.purpose && params.purpose !== "ALL") query.append("purpose", params.purpose);
  if (params.logged_by && params.logged_by !== "ALL") query.append("logged_by", params.logged_by.toString());
  if (params.start_date) query.append("start_date", params.start_date);
  if (params.end_date) query.append("end_date", params.end_date);
  if (params.scope) query.append("scope", params.scope);

  const queryString = query.toString();
  const url = queryString ? `/visitors/?${queryString}` : "/visitors/";
  const res = await client.get(url, { signal });
  return res.data;
};

/**
 * Fetch single visitor log details
 */
export const fetchVisitorById = async (id: number): Promise<VisitorLogItem> => {
  const client = getClient();
  if (!client) throw new Error("API client not initialized");

  const res = await client.get(`/visitors/${id}/`);
  return res.data;
};

/**
 * Create a new visitor log entry
 */
export const createVisitor = async (payload: CreateVisitorPayload): Promise<VisitorLogItem> => {
  const client = getClient();
  if (!client) throw new Error("API client not initialized");

  const res = await client.post("/visitors/", payload);
  return res.data;
};

/**
 * Update an existing visitor log entry
 */
export const updateVisitor = async (id: number, payload: UpdateVisitorPayload): Promise<VisitorLogItem> => {
  const client = getClient();
  if (!client) throw new Error("API client not initialized");

  const res = await client.patch(`/visitors/${id}/`, payload);
  return res.data;
};

/**
 * Fast action: Mark visitor as checked out
 */
export const markVisitorCheckedOut = async (id: number): Promise<VisitorLogItem> => {
  return updateVisitor(id, { action: "check_out" });
};

/**
 * Fast action: Mark visitor as checked in
 */
export const markVisitorCheckedIn = async (id: number): Promise<VisitorLogItem> => {
  return updateVisitor(id, { action: "check_in" });
};

/**
 * Update visitor status (e.g., checkout, cancel, enquiry)
 */
export const updateVisitorStatus = async (
  id: number,
  status: VisitorStatus,
  notes?: string
): Promise<VisitorLogItem> => {
  return updateVisitor(id, { status, notes });
};

/**
 * Delete a visitor record
 */
export const deleteVisitor = async (id: number): Promise<void> => {
  const client = getClient();
  if (!client) throw new Error("API client not initialized");

  await client.delete(`/visitors/${id}/`);
};
